import { ExtendedFirebaseInstance } from 'react-redux-firebase'
import { getEnvVariables } from '../config/getEnvVars'
import { actions as appActions } from '../features/App/reducer'
import { clientLogger } from './observability'

export interface ApiConfig {
  method: string
  headers: Record<string, string>
  body: string
}

interface Props {
  body: any
}

type FirebaseUserWithToken = {
  getIdToken: (forceRefresh?: boolean) => Promise<string>
}

type FirebaseAuthLike = {
  currentUser: FirebaseUserWithToken | null
  onAuthStateChanged: (
    nextOrObserver: (user: FirebaseUserWithToken | null) => void,
    error?: (error: unknown) => void
  ) => () => void
}

const AUTH_USER_TIMEOUT_MS = 5000

const getFirebaseAuth = () =>
  (
    window.firebase as ExtendedFirebaseInstance & {
      auth: () => FirebaseAuthLike
    }
  ).auth()

const onAuthorizeError = (error: unknown) => {
  clientLogger.error('auth.authorize.failed', { error })
  window.store.dispatch(
    appActions.onAlert({
      severity: 'error',
      message: 'Could Not Authorize',
    })
  )
}

const waitForCurrentUser = async (): Promise<FirebaseUserWithToken | null> => {
  const auth = getFirebaseAuth()

  if (auth.currentUser != null) {
    return auth.currentUser
  }

  return await new Promise((resolve) => {
    const timeoutId = window.setTimeout(() => {
      unsubscribe()
      resolve(auth.currentUser)
    }, AUTH_USER_TIMEOUT_MS)

    const unsubscribe = auth.onAuthStateChanged(
      (user) => {
        if (user == null) {
          return
        }

        window.clearTimeout(timeoutId)
        unsubscribe()
        resolve(user)
      },
      () => {
        window.clearTimeout(timeoutId)
        unsubscribe()
        resolve(null)
      }
    )
  })
}

const getIdToken = async () => {
  const currentUser = await waitForCurrentUser()
  if (currentUser == null) {
    clientLogger.warn('auth.id_token.missing_current_user')
    return undefined
  }
  return await currentUser.getIdToken(true)
}

export const getAuthToken = async (): Promise<string | undefined> => {
  try {
    return await getIdToken()
  } catch (error) {
    onAuthorizeError(error)
    return undefined
  }
}

export const getApiConfig = async ({ body }: Props) => {
  try {
    const idToken = await getAuthToken()
    if (idToken == null) return undefined

    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify(body),
    }
  } catch (error) {
    onAuthorizeError(error)
    return undefined
  }
}

/** Returns auth headers for GET requests (e.g. report/audit). Does not include body. */
export const getAuthHeaders = async (): Promise<
  Record<string, string> | undefined
> => {
  try {
    const idToken = await getAuthToken()
    if (idToken == null) return undefined
    return {
      'Content-Type': 'application/json; charset=utf-8',
      Authorization: `Bearer ${idToken}`,
    }
  } catch (error) {
    onAuthorizeError(error)
    return undefined
  }
}

const normalizeHostname = (hostname: string) =>
  hostname
    .trim()
    .toLowerCase()
    .replace(/^\[(.*)\]$/, '$1')

const isLoopbackHost = (hostname: string) =>
  ['localhost', '127.0.0.1', '0.0.0.0', '::1'].includes(
    normalizeHostname(hostname)
  )

const formatHostnameForURL = (hostname: string) => {
  const normalized = normalizeHostname(hostname)

  return normalized.includes(':') ? `[${normalized}]` : normalized
}

export const getLocalServerURL = (
  path: string,
  hostname = window.location.hostname
) => {
  if (isLoopbackHost(hostname)) {
    return `http://${formatHostnameForURL(hostname)}:8000${path}`
  }

  return `${getEnvVariables({ hostname }).SERVER_URL}${path}`
}
